import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { http } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { CellTower, ShieldCheck, User, Users, Key, Plus, PencilSimple } from "@phosphor-icons/react";

/*  Super-Admin only: manage per-NAS owner logins.
    Hierarchy: Super Admin → NAS Login → NAS Employee.
    A NAS Login is a nas_staff row with role='nas_admin' — one per NAS.
    From that account, the NAS owner signs in at /staff/login and manages
    their own NAS's subscribers + employees, scoped by the NAS-guard already
    in place server-side. */
export default function NasLogins() {
  const [rows, setRows] = useState([]);
  const [editing, setEditing] = useState(null); // { nas, owner }
  const [form, setForm] = useState({ name: "", email: "", phone: "", password: "" });
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const { data } = await http.get("/nas-logins");
      setRows(data || []);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to load NAS logins"); }
  };
  useEffect(() => { load(); }, []);

  const openEditor = (r) => {
    setEditing(r);
    setForm({
      name:  r.owner?.name  || "",
      email: r.owner?.email || "",
      phone: r.owner?.phone || "",
      password: "",
    });
  };
  const closeEditor = () => { setEditing(null); setForm({ name: "", email: "", phone: "", password: "" }); };

  const save = async (e) => {
    e.preventDefault();
    if (!editing) return;
    try {
      setSaving(true);
      const payload = { ...form };
      if (!payload.password) delete payload.password;
      const { data } = await http.post(`/nas/${editing.nas.id}/login`, payload);
      toast.success(`NAS Login ${data.action}`);
      closeEditor(); load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-4 fade-up" data-testid="nas-logins-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="uppercase-label">Access hierarchy</div>
          <h1 className="font-display font-bold text-4xl mt-1">NAS Logins</h1>
          <p className="text-sm text-muted-foreground mt-2 max-w-2xl">
            Each NAS has one primary owner login. That account signs in at <code className="font-mono text-primary">/staff/login</code>, sees only its own NAS's subscribers + employees, and can create additional employees under it. Super Admin (you) sits above all NAS Logins.
          </p>
        </div>
      </div>

      <div className="border border-border bg-card overflow-x-auto">
        <table className="w-full text-sm min-w-[720px]">
          <thead>
            <tr className="bg-[hsl(var(--muted))] border-b border-border text-xs uppercase font-mono">
              <th className="text-left py-3 px-4"><CellTower size={12} className="inline mr-1" /> NAS</th>
              <th className="text-left py-3 px-4">Owner login</th>
              <th className="text-left py-3 px-4">Email</th>
              <th className="text-right py-3 px-4">Subscribers</th>
              <th className="text-right py-3 px-4">Employees</th>
              <th className="text-right py-3 px-4">Action</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan={6} className="text-center py-10 text-muted-foreground text-sm">No NAS devices yet — create one under NAS Devices first.</td></tr>}
            {rows.map((r) => (
              <tr key={r.nas.id} className="border-b border-border last:border-b-0 hover:bg-accent/30" data-testid={`nas-login-row-${r.nas.id}`}>
                <td className="py-3 px-4">
                  <div className="font-medium">{r.nas.name}</div>
                  <div className="text-[11px] text-muted-foreground font-mono">{r.nas.nas_ip}</div>
                </td>
                <td className="py-3 px-4">
                  {r.owner ? (
                    <span className="inline-flex items-center gap-2 text-sm">
                      <ShieldCheck size={13} className="text-primary" /> {r.owner.name}
                    </span>
                  ) : (
                    <span className="text-xs text-muted-foreground italic">not configured</span>
                  )}
                </td>
                <td className="py-3 px-4 font-mono text-xs text-muted-foreground">{r.owner?.email || "—"}</td>
                <td className="py-3 px-4 text-right font-mono">{r.subscribers_count}</td>
                <td className="py-3 px-4 text-right font-mono">{r.employees_count}</td>
                <td className="py-3 px-4 text-right">
                  <div className="flex items-center justify-end gap-2">
                    <Link
                      to={`/staff?nas_id=${r.nas.id}`}
                      data-testid={`nas-add-emp-${r.nas.id}`}
                      className="inline-flex items-center gap-1 border border-border rounded-sm h-8 px-2 text-xs hover:bg-accent/40"
                      title="Add / manage employees under this NAS"
                    >
                      <Users size={11} /> Add employee
                    </Link>
                    <Button
                      size="sm"
                      variant={r.owner ? "outline" : "default"}
                      onClick={() => openEditor(r)}
                      className="rounded-sm h-8 text-xs"
                      data-testid={`nas-login-edit-${r.nas.id}`}
                    >
                      {r.owner ? (<><PencilSimple size={11} className="mr-1" /> Edit</>) : (<><Plus size={11} className="mr-1" /> Create login</>)}
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Editor modal */}
      <Dialog open={!!editing} onOpenChange={(v) => !v && closeEditor()}>
        <DialogContent className="bg-card border-border max-w-md" data-testid="nas-login-modal">
          <DialogHeader><DialogTitle className="font-display">{editing?.owner ? "Edit" : "Create"} NAS Login — {editing?.nas?.name}</DialogTitle></DialogHeader>
          <form onSubmit={save} className="space-y-3">
            <div>
              <Label className="uppercase-label">Full name</Label>
              <Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1.5" data-testid="nl-name" autoFocus />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="uppercase-label">Email</Label>
                <Input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1.5" data-testid="nl-email" />
              </div>
              <div>
                <Label className="uppercase-label">Phone</Label>
                <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mt-1.5" data-testid="nl-phone" />
              </div>
            </div>
            <div>
              <Label className="uppercase-label">{editing?.owner ? "Reset password (leave blank to keep)" : "Password"}</Label>
              <Input
                type="text"
                required={!editing?.owner}
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                className="mt-1.5 font-mono"
                data-testid="nl-password"
                placeholder={editing?.owner ? "leave empty to keep current" : "min 8 chars"}
              />
            </div>
            <Button type="submit" disabled={saving} className="w-full rounded-sm" data-testid="nl-submit">
              <Key size={14} className="mr-1.5" /> {saving ? "Saving…" : (editing?.owner ? "Update NAS Login" : "Create NAS Login")}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
